/**
 * schemaMarkup utilities — build Schema.org JSON-LD objects for pSEO pages
 */

/**
 * Build a BlogPosting schema for a blog post page
 * @param {object} opts - { post, brand }
 * @returns {object}
 */
export function buildBlogPostSchema({ post, brand }) {
  if (!post) return null;
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title || '',
    "datePublished": post.date || '',
    "dateModified": post.date || '',
    "author": {
      "@type": "Organization",
      "name": brand ? brand.name : 'Orthopedic Specialists',
    },
    "publisher": {
      "@type": "Organization",
      "name": brand ? brand.name : 'Orthopedic Specialists',
    },
    "description": post.excerpt || '',
    "url": post.url || '',
    "image": post.images && post.images[0] ? post.images[0] : undefined,
  };
}

/**
 * Build a MedicalWebPage schema for a condition or body-part page
 * @param {object} opts - { condition, brand }
 * @returns {object}
 */
export function buildConditionPageSchema({ condition, brand }) {
  if (!condition) return null;
  return {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    "name": condition.display || '',
    "description": condition.description || `Learn about ${condition.display || 'this condition'} treatment options.`,
    "publisher": {
      "@type": "Organization",
      "name": brand ? brand.name : 'Orthopedic Specialists',
    },
    "about": {
      "@type": "MedicalCondition",
      "name": condition.display || '',
    },
  };
}

/**
 * Build a FAQPage schema from Q&A pairs
 * @param {object} opts - { faqs: [{question, answer}] }
 * @returns {object}
 */
export function buildFAQPageSchema({ faqs }) {
  if (!faqs || !faqs.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question || '',
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer || '',
      },
    })),
  };
}

/**
 * Build a MedicalOrganization schema for the home page
 */
export function buildOrganizationSchema({ brand }) {
  if (!brand) return null;
  return {
    "@context": "https://schema.org",
    "@type": "MedicalOrganization",
    name: brand.name,
    url: `https://${brand.domain}`,
    telephone: brand.phone,
    logo: `https://${brand.domain}/assets/brand/${brand.brandKey}/logo-color.webp`,
    address: brand.offices?.[0] ? {
      "@type": "PostalAddress",
      streetAddress: brand.offices[0].address
    } : undefined,
    medicalSpecialty: ["Orthopedics", "Pain Management", "Physical Therapy", "Neurology"]
  };
}

/**
 * Build a MedicalClinic schema for a location page
 */
export function buildLocationSchema({ brand, location }) {
  if (!brand || !location) return null;
  return {
    "@context": "https://schema.org",
    "@type": "MedicalClinic",
    name: `${brand.name} - ${location.city}`,
    url: `https://${brand.domain}/${location.slug}`,
    telephone: brand.phone,
    address: {
      "@type": "PostalAddress",
      streetAddress: location.address,
      addressLocality: location.city,
      addressRegion: location.state
    },
    geo: location.lat && location.lng ? {
      "@type": "GeoCoordinates",
      latitude: location.lat,
      longitude: location.lng
    } : undefined
  };
}

/**
 * Build a Blog schema for the blog index page
 */
export function buildBlogIndexSchema({ brand, articles }) {
  if (!brand) return null;
  return {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: `${brand.name} Blog`,
    url: `https://${brand.domain}/blog`,
    blogPost: (articles || []).slice(0, 10).map(a => ({
      "@type": "BlogPosting",
      headline: a.title,
      url: `https://${brand.domain}/blog/${a.slug}`
    }))
  };
}

/**
 * Build a MedicalBusiness schema for a city × service page
 * @param {object} opts - { brand, service, location }
 * @returns {object} Schema.org JSON-LD object
 */
export function buildServicePageSchema({ brand, service, location }) {
  if (!brand || !service || !location) return null;
  return {
    "@context": "https://schema.org",
    "@type": "MedicalBusiness",
    "name": `${brand.name} - ${service.display} in ${location.city}`,
    "description": `Expert ${(service.display || '').toLowerCase()} in ${location.city}, ${location.state}. Free consultation.`,
    "address": location.address ? {
      "@type": "PostalAddress",
      "streetAddress": location.address,
      "addressLocality": location.city,
      "addressRegion": location.state,
      "addressCountry": "US",
    } : undefined,
    "geo": (location.lat && location.lng) ? {
      "@type": "GeoCoordinates",
      "latitude": location.lat,
      "longitude": location.lng,
    } : undefined,
    "telephone": brand.phone,
    "url": `https://${brand.domain}/${location.slug}/${service.slug}`,
    "areaServed": { "@type": "City", "name": location.city },
    "medicalSpecialty": service.category,
  };
}
